// src/pages/Register.tsx
// Full coach registration flow — supports online (mock) and prepaid (bank transfer) payments
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAllCompetitions } from "../firebase/competitions";
import { getAllClubs } from "../firebase/clubs";
import { getCoachOrganizationAccess, getOrganizations } from "../firebase/organizationFoundation";
import { createAthleteAsCoach } from "../firebase/athletes";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import Navbar from "../components/Navbar";
import { useAthleteSearch, Athlete } from "../hooks/useAthleteSearch";
import { calculatePrice } from "../utils/priceCalculator";
import { formatDateGeorgian } from "../utils/dateHelpers";
import { categoryDisplayLabel } from "../utils/rankingCalculator";
import { getRecommendedWeights } from "../utils/weightSuggestions";
import WeightSelect from "../components/WeightSelect";
import {
  createPrepaidRegistrationBatch,
  isAthleteRegisteredByCoach,
} from "../firebase/registrations";
import {
  getActiveCreditPackageForCoach,
  CreditPackage,
} from "../firebase/creditPackages";
import { Timestamp } from "firebase/firestore";
import { findPossibleAthleteMatches } from "../utils/duplicateAthletes";
import { getFunctions, httpsCallable } from "firebase/functions";
import { C, FONT } from "../theme";
import "./RegisterBasket.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RegistrationItem {
  athleteId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  club: string;
  division: "Male" | "Female";
  weightCategory: string;
  category: string;
  price: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(ts: Timestamp, localeStr: string): string {
  const d = ts.toDate();
  if (localeStr === "ka") return formatDateGeorgian(d);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * Builds the HTML confirmation email for prepaid (bank transfer) registration.
 * Bilingual EN/KA with federation logo (Phase 16).
 */
function buildPrepaidConfirmationEmailHtml(params: {
  coachEmail: string;
  competitionName: string;
  referenceCode: string;
  athletes: RegistrationItem[];
}): string {
  const { competitionName, referenceCode, athletes } = params;

  const athleteRows = athletes
    .map(
      (a, i) => `
      <tr style="background:${i % 2 === 0 ? "#f8fafc" : "#ffffff"}">
        <td style="padding:10px 14px;font-size:13px;color:#0f172a;font-weight:600;">${a.firstName} ${a.lastName}</td>
        <td style="padding:10px 14px;font-size:13px;color:#475569;">${a.club}</td>
        <td style="padding:10px 14px;font-size:13px;color:#475569;">${categoryDisplayLabel(a.category)}</td>
        <td style="padding:10px 14px;font-size:13px;color:#475569;">${a.division}</td>
        <td style="padding:10px 14px;font-size:13px;color:#475569;">${a.weightCategory}</td>
        <td style="padding:10px 14px;font-size:13px;color:#0f172a;font-weight:700;text-align:right;">${a.price} ₾</td>
      </tr>`
    )
    .join("");

  return `
<!DOCTYPE html>
<html lang="ka">
<head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/></head>
<body style="margin:0;padding:0;background-color:#f1f5f9;font-family:'Segoe UI',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f1f5f9;padding:32px 16px;">
    <tr><td align="center">
      <table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);border:1px solid #e2e8f0;">
        <!-- HEADER -->
        <tr>
          <td style="background:linear-gradient(135deg,#da291c 0%,var(--color-danger) 100%);padding:32px 32px 24px;text-align:center;">
            <img src="https://registration.taekwondo.ge/geo.png"
                 alt="Georgian Taekwondo Federation"
                 width="72" height="72"
                 style="width:72px;height:72px;object-fit:contain;display:block;margin:0 auto 14px;opacity:0.95;" />
            <div style="color:#ffffff;font-size:20px;font-weight:800;letter-spacing:2px;text-transform:uppercase;margin-bottom:4px;">GEORGIAN TAEKWONDO</div>
            <div style="color:rgba(255,255,255,0.70);font-size:12px;letter-spacing:1.5px;text-transform:uppercase;margin-bottom:8px;">საქართველოს ტაეკვონდოს ფედერაცია</div>
            <div style="color:rgba(255,255,255,0.85);font-size:13px;letter-spacing:0.5px;">Registration Confirmation / რეგისტრაციის დადასტურება</div>
          </td>
        </tr>
        <!-- BADGE -->
        <tr>
          <td style="padding:24px 32px 0;text-align:center;">
            <div style="display:inline-block;background:var(--color-success-soft);border:1px solid #6ee7b7;border-radius:24px;padding:8px 20px;font-size:13px;font-weight:700;color:var(--color-success);letter-spacing:0.5px;">
              ✓ &nbsp;Paid via Bank Transfer / გადახდილია საბანკო გადარიცხვით
            </div>
          </td>
        </tr>
        <!-- COMPETITION + REFERENCE -->
        <tr>
          <td style="padding:20px 32px;">
            <table width="100%" cellpadding="0" cellspacing="0" style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;">
              <tr>
                <td style="padding:16px 20px;">
                  <div style="font-size:10px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;">Competition / შეჯიბრება</div>
                  <div style="font-size:16px;font-weight:700;color:#0f172a;">${competitionName}</div>
                </td>
                <td style="padding:16px 20px;text-align:right;border-left:1px solid #e2e8f0;">
                  <div style="font-size:10px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;">Reference Code / საცნობარო კოდი</div>
                  <div style="font-size:16px;font-weight:800;color:#da291c;letter-spacing:1px;">${referenceCode}</div>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <!-- ATHLETES TABLE -->
        <tr>
          <td style="padding:0 32px 20px;">
            <div style="font-size:10px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;">
              Registered Athletes (${athletes.length}) / რეგისტრირებული სპორტსმენები (${athletes.length})
            </div>
            <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;">
              <thead>
                <tr style="background:#f1f5f9;">
                  <th style="padding:10px 14px;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:left;">Athlete / სპორტსმენი</th>
                  <th style="padding:10px 14px;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:left;">Club / კლუბი</th>
                  <th style="padding:10px 14px;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:left;">Category / კატეგორია</th>
                  <th style="padding:10px 14px;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:left;">Division / სქესი</th>
                  <th style="padding:10px 14px;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:left;">Weight / წონა</th>
                  <th style="padding:10px 14px;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:right;">Price / ფასი</th>
                </tr>
              </thead>
              <tbody>${athleteRows}</tbody>
            </table>
          </td>
        </tr>
        <!-- CONFIRMATION NOTE -->
        <tr>
          <td style="padding:0 32px 24px;">
            <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:16px 20px;">
              <div style="font-size:13px;font-weight:700;color:#166534;margin-bottom:8px;">✓ Payment Confirmed / ✓ გადახდა დადასტურებულია</div>
              <div style="font-size:13px;color:#166534;line-height:1.8;">
                Your bank transfer payment has been received and your registration is confirmed.<br/>
                თქვენი საბანკო გადარიცხვა მიღებულია და რეგისტრაცია დადასტურებულია.
              </div>
              <div style="margin-top:14px;">
                <a href="https://registration.taekwondo.ge/history"
                   style="display:inline-block;background:#da291c;color:#ffffff;font-size:13px;font-weight:700;padding:10px 20px;border-radius:8px;text-decoration:none;letter-spacing:0.3px;">
                  View My Registration History / ჩემი რეგისტრაციების ნახვა →
                </a>
              </div>
            </div>
          </td>
        </tr>
        <!-- FOOTER -->
        <tr>
          <td style="background:#f8fafc;border-top:1px solid #e2e8f0;padding:20px 32px;text-align:center;">
            <div style="font-size:12px;color:#94a3b8;line-height:1.8;">
              This is an automated confirmation from the Georgian Taekwondo Federation.<br/>
              ეს არის ქართული ტაეკვონდოს ფედერაციის ავტომატური შეტყობინება.<br/>
              For questions / კითხვებისთვის:
              <a href="mailto:taekwondogeo@gmail.com" style="color:#da291c;text-decoration:none;font-weight:600;">taekwondogeo@gmail.com</a>
            </div>
            <div style="margin-top:10px;">
              <a href="https://taekwondo.ge" style="font-size:12px;color:#94a3b8;text-decoration:none;">taekwondo.ge</a>
              &nbsp;·&nbsp;
              <a href="https://registration.taekwondo.ge" style="font-size:12px;color:#94a3b8;text-decoration:none;">registration.taekwondo.ge</a>
            </div>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

// ---------------------------------------------------------------------------
// Confirmation Modal
// ---------------------------------------------------------------------------

interface ConfirmModalProps {
  competition: any;
  registrationList: RegistrationItem[];
  totalAmount: number;
  saving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  t: (key: string) => string;
  gel: string;
  creditPackage: CreditPackage | null;
  /** When true the modal is already in prepaid mode — no toggle shown */
  prepaidMode: boolean;
  paymentMethod: "online" | "prepaid";
  onPaymentMethodChange: (m: "online" | "prepaid") => void;
  /** When true, a deadline crossed since the basket was built; total updated. */
  priceTierChanged: boolean;
}

function ConfirmModal({
  competition,
  registrationList,
  totalAmount,
  saving,
  onConfirm,
  onCancel,
  t,
  gel,
  creditPackage,
  prepaidMode,
  paymentMethod,
  onPaymentMethodChange,
  priceTierChanged,
}: ConfirmModalProps) {
  const remaining = creditPackage
    ? creditPackage.creditsTotal - creditPackage.creditsUsed
    : 0;

  // Only show the toggle when NOT in prepaidMode but credits exist and cover basket
  const enoughCredits = remaining >= registrationList.length;
  const showPrepaidToggle = !prepaidMode && creditPackage !== null && enoughCredits;
  const insufficientCredits = !prepaidMode && creditPackage !== null && !enoughCredits;

  // Effective payment: prepaidMode forces prepaid
  const effectivePrepaid = prepaidMode || paymentMethod === "prepaid";

  return (
    <div style={ms.overlay}>
      <div style={ms.modal}>
        <div style={ms.accentBar} />

        <div style={ms.modalBody}>
          <div style={ms.modalHeader}>
            <div style={ms.modalTitle}>{t("register.confirm.title")}</div>
            <div style={ms.modalSubtitle}>{t("register.confirm.subtitle")}</div>
          </div>

          <div style={ms.infoRow}>
            <span style={ms.infoLabel}>{t("register.confirm.competition")}</span>
            <span style={ms.infoValue}>{competition.name}</span>
          </div>

          {priceTierChanged && (
            <div
              style={{
                backgroundColor: C.warningDim,
                border: `1px solid ${C.warningBorder}`,
                borderRadius: "8px",
                padding: "10px 14px",
                fontSize: "13px",
                color: "var(--color-warning)",
                lineHeight: 1.5,
              }}
            >
              ⚠️ Price tier changed — a deadline passed since you added these
              athletes. Basket totals were updated to today's price.
            </div>
          )}

          <div style={ms.athletesSection}>
            <div style={ms.athletesSectionLabel}>
              {t("register.confirm.athletes")} ({registrationList.length})
            </div>
            <div style={ms.athletesList}>
              {registrationList.map((item, idx) => (
                <div key={item.athleteId} style={ms.athleteRow}>
                  <div style={ms.athleteRowLeft}>
                    <span style={ms.athleteIndex}>{idx + 1}</span>
                    <div>
                      <div style={ms.athleteName}>
                        {item.firstName} {item.lastName}
                      </div>
                      <div style={ms.athleteMeta}>
                        {item.club} · {categoryDisplayLabel(item.category)} ·{" "}
                        {item.division} · {item.weightCategory}
                      </div>
                    </div>
                  </div>
                  <div style={ms.athletePrice}>
                    {item.price} {gel}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={ms.totalRow}>
            <span style={ms.totalLabel}>{t("register.confirm.total")}</span>
            <span style={ms.totalAmount}>
              {effectivePrepaid ? (
                <>
                  <span style={{ textDecoration: "line-through", color: "var(--color-text-subtle)", fontSize: "16px" }}>
                    {totalAmount} {gel}
                  </span>
                  {" "}
                  <span style={{ color: "var(--color-success)" }}>0 {gel}</span>
                </>
              ) : (
                `${totalAmount} ${gel}`
              )}
            </span>
          </div>

          {/* ── Prepaid mode: show info box, no toggle ── */}
          {prepaidMode && creditPackage && (
            <div style={ms.prepaidInfoBox}>
              <div style={ms.prepaidInfoTitle}>🎟️ {t("register.prepaidMode.modalTitle")}</div>
              <div style={ms.prepaidInfoSub}>
                {t("register.prepaidMode.modalInfo")
                  .replace("{count}", String(registrationList.length))
                  .replace("{remaining}", String(remaining))}
              </div>
            </div>
          )}

          {/* ── Payment method toggle (only when not in prepaidMode but credits cover basket) ── */}
          {showPrepaidToggle && (
            <div style={ms.paymentSection}>
              <div style={ms.paymentSectionLabel}>
                {t("register.payment.method")}
              </div>
              <div style={ms.paymentOptions}>
                <button
                  style={{
                    ...ms.paymentOptionBtn,
                    ...(paymentMethod === "online"
                      ? ms.paymentOptionActive
                      : ms.paymentOptionInactive),
                  }}
                  onClick={() => onPaymentMethodChange("online")}
                  disabled={saving}
                >
                  <div style={ms.paymentOptionTitle}>
                    {t("register.payment.online")}
                  </div>
                  <div style={ms.paymentOptionSub}>
                    {t("register.payment.onlineSub")}
                  </div>
                </button>
                <button
                  style={{
                    ...ms.paymentOptionBtn,
                    ...(paymentMethod === "prepaid"
                      ? ms.paymentOptionPrepaidActive
                      : ms.paymentOptionInactive),
                  }}
                  onClick={() => onPaymentMethodChange("prepaid")}
                  disabled={saving}
                >
                  <div style={ms.paymentOptionTitle}>
                    {t("register.payment.prepaid")}
                  </div>
                  <div style={ms.paymentOptionSub}>
                    {t("register.payment.prepaidSub")
                      .replace("{remaining}", String(remaining))
                      .replace("{count}", String(registrationList.length))}
                  </div>
                </button>
              </div>
            </div>
          )}

                    {/* Warning: non-prepaid mode, not enough credits */}
          {insufficientCredits && (
            <div style={ms.creditWarning}>
              {t("register.payment.insufficientCredits")
                .replace("{remaining}", String(remaining))
                .replace("{needed}", String(registrationList.length - remaining))}
            </div>
          )}

          {/* Warning: prepaid mode but basket EXCEEDS credits — blocks confirm */}
          {prepaidMode && remaining < registrationList.length && (
            <div style={ms.prepaidOverLimitWarning}>
              ⚠️ {t("register.prepaidMode.overLimitError")}
            </div>
          )}

          <div style={ms.actions}>
            <button style={ms.cancelBtn} onClick={onCancel} disabled={saving}>
              {t("register.confirm.cancel")}
            </button>
            <button
              style={
                saving || (prepaidMode && remaining < registrationList.length)
                  ? ms.confirmBtnDisabled
                  : ms.confirmBtn
              }
              onClick={onConfirm}
              disabled={saving || (prepaidMode && remaining < registrationList.length)}
            >
              {saving
                ? t("register.saving")
                : effectivePrepaid
                ? t("register.confirm.proceedPrepaid")
                : t("register.confirm.proceed")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function Register() {
  const { competitionId } = useParams<{ competitionId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const locale = localStorage.getItem("language") || "en";

  // -- Competition + clubs --
  const [competition, setCompetition] = useState<any>(null);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [clubs, setClubs] = useState<any[]>([]);
  const [authorizedClubNames, setAuthorizedClubNames] = useState<string[]>([]);
  const [authorizedOrganizations, setAuthorizedOrganizations] = useState<Array<{ id: string; officialName: string }>>([]);
  const [pageLoading, setPageLoading] = useState<boolean>(true);

  // -- Credit package --
  const [creditPackage, setCreditPackage] = useState<CreditPackage | null>(null);

  // -- Prepaid mode: coach explicitly activated prepaid before adding athletes --
  const [prepaidMode, setPrepaidMode] = useState<boolean>(false);

  // -- Athlete search --
  const {
    athletes,
    filtered,
    search,
    setSearch,
    loading: athletesLoading,
  } = useAthleteSearch();

  // -- Form state --
  const [selectedAthlete, setSelectedAthlete] = useState<Athlete | null>(null);
  const [selectedClub, setSelectedClub] = useState<string>("");
  const [selectedDivision, setSelectedDivision] = useState<"Male" | "Female">("Male");
  const [selectedWeight, setSelectedWeight] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [formError, setFormError] = useState<string>("");
  const [duplicateWarning, setDuplicateWarning] = useState<string>("");
  const [hoveredResult, setHoveredResult] = useState<string>("");
  const [clubFocused, setClubFocused] = useState<boolean>(false);

  // -- Checking duplicate in Firestore --
  const [checkingDuplicate, setCheckingDuplicate] = useState<boolean>(false);

  // -- Registration basket --
  const [registrationList, setRegistrationList] = useState<RegistrationItem[]>([]);

  // -- Confirmation modal & payment --
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<"online" | "prepaid">("online");
  // Set when "Proceed to payment" recomputed the price and found it changed
  // (e.g. an early/mid deadline passed between basket-add and confirm).
  // Cleared when the modal is closed/cancelled or the user successfully
  // confirms. Drives a banner inside the modal.
  const [priceTierChanged, setPriceTierChanged] = useState<boolean>(false);

  // New state for create athlete flow
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ firstName: "", lastName: "", dateOfBirth: "" });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [duplicateOverrideConfirmed, setDuplicateOverrideConfirmed] = useState(false);
  const [duplicateOverrideReason, setDuplicateOverrideReason] = useState("");

  const possibleCreateMatches = useMemo(() => findPossibleAthleteMatches(athletes, createForm), [athletes, createForm]);
  const hasStrongCreateMatch = possibleCreateMatches.some((match) => match.strength === "strong");

  // Load competition + clubs + credit package
  useEffect(() => {
    if (!currentUser || !competitionId) return;

    Promise.all([
      getAllCompetitions(),
      getAllClubs(),
      getActiveCreditPackageForCoach(currentUser.uid, competitionId),
    ])
      .then(([comps, clubList, activePkg]) => {
        const found = comps.find((c: any) => c.id === competitionId);
        if (found) {
          setCompetition(found);
          setCurrentPrice(calculatePrice(found as any));
        }
        setClubs(clubList);
        setCreditPackage(activePkg);
      })
      .catch((err) => console.error("Failed to load page data:", err))
      .finally(() => setPageLoading(false));
  }, [competitionId, currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    let active = true;
    Promise.all([getCoachOrganizationAccess(currentUser.uid), getOrganizations()])
      .then(([access, organizationRows]) => {
        if (!active) return;
        const ids = [...new Set([
          ...access.registrationOrganizationIds,
          ...access.rosterOrganizationIds,
          ...access.regionalRegistrationOrganizationIds,
        ])];
        const authorized = organizationRows
          .filter((organization) => ids.includes(organization.id) && organization.status === "ACTIVE")
          .map((organization) => ({ id: organization.id, officialName: organization.officialName }));
        const names = authorized.map((organization) => organization.officialName);
        setAuthorizedOrganizations(authorized);
        setAuthorizedClubNames(names);
        if (names.length === 1) setSelectedClub(names[0]);
      })
      .catch((accessError) => {
        // Transitional fallback preserves the live legacy picker until the new
        // rules and the coach's real grants have both been deployed.
        console.info("Official coach club access is not available yet; using legacy club selection.", accessError);
      });
    return () => { active = false; };
  }, [currentUser]);

  // If credit package disappears (exhausted externally), exit prepaid mode
  useEffect(() => {
    if (!creditPackage || creditPackage.creditsUsed >= creditPackage.creditsTotal) {
      setPrepaidMode(false);
    }
  }, [creditPackage]);

  // Reset weight when division changes
  useEffect(() => {
    setSelectedWeight("");
  }, [selectedDivision]);

  // Reset category when competition changes
  useEffect(() => {
    if (competition?.types?.length === 1) {
      setSelectedCategory(competition.types[0]);
    } else {
      setSelectedCategory("");
    }
  }, [competition]);

  const weightOptions: string[] =
    competition?.weightCategories?.[selectedDivision.toLowerCase()] ?? [];

  const typeOptions: string[] = competition?.types ?? [];

  const availableClubs = authorizedClubNames.length > 0
    ? authorizedClubNames.map((name) => ({ id: `authorized-${name}`, name }))
    : clubs;
  const filteredClubs = selectedClub
    ? availableClubs.filter((c: any) =>
        c.name.toLowerCase().includes(selectedClub.toLowerCase())
      )
    : availableClubs;

  const gel = t("admin.competitions.labels.gel");

  const creditsRemaining = creditPackage
    ? creditPackage.creditsTotal - creditPackage.creditsUsed
    : 0;

  /** True when in prepaid mode and the basket has hit the credit ceiling */
  const prepaidLimitReached = prepaidMode && registrationList.length >= creditsRemaining;

  // -- Handlers --

  const handleCreateAthlete = async () => {
    setCreateError("");
    if (!createForm.firstName.trim()) {
      setCreateError(t("register.create.errorFirstName"));
      return;
    }
    if (!createForm.lastName.trim()) {
      setCreateError(t("register.create.errorLastName"));
      return;
    }
    if (possibleCreateMatches.length > 0 && !duplicateOverrideConfirmed) {
      setDuplicateOverrideConfirmed(true);
      setCreateError(t("register.createAthleteModal.reviewBeforeCreate"));
      return;
    }
    if (hasStrongCreateMatch && duplicateOverrideReason.trim().length < 8) {
      setCreateError(t("register.createAthleteModal.reasonRequired"));
      return;
    }

    setCreateLoading(true);
    try {
      const result = await createAthleteAsCoach({
        firstName: createForm.firstName.trim(),
        lastName:  createForm.lastName.trim(),
        dateOfBirth: createForm.dateOfBirth.trim(),
        duplicateReviewReason: duplicateOverrideReason.trim(),
        organizationId: authorizedOrganizations.find((organization) => organization.officialName === selectedClub)?.id,
      });

        const newAthlete: Athlete = {
        id:          result.docId,
        athleteId:   result.athleteId,
        firstName:   createForm.firstName.trim(),
        lastName:    createForm.lastName.trim(),
        dateOfBirth: createForm.dateOfBirth.trim(),
      };

      setShowCreateModal(false);
      setCreateForm({ firstName: "", lastName: "", dateOfBirth: "" });
      setDuplicateOverrideConfirmed(false);
      setDuplicateOverrideReason("");
      setSearch("");
      handleSelectAthlete(newAthlete);
    } catch (err: any) {
      setCreateError(err?.message ?? t("register.create.errorGeneric"));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleActivatePrepaidMode = () => {
    setPrepaidMode(true);
    setFormError("");
  };

  const handleExitPrepaidMode = () => {
    setPrepaidMode(false);
    setPaymentMethod("online");
    setFormError("");
  };

  const handleSelectAthlete = (athlete: Athlete) => {
    setSelectedAthlete(athlete);
    setSearch("");
    setFormError("");

    const isDuplicate = registrationList.some(
      (r) => r.athleteId === athlete.athleteId
    );
    setDuplicateWarning(
      isDuplicate
        ? `${athlete.firstName} ${athlete.lastName} ${t("register.duplicateWarning")}`
        : ""
    );
  };

  const handleAddAthlete = async () => {
    setFormError("");

    // In prepaid mode, enforce the credit limit BEFORE any other validation
    if (prepaidMode && registrationList.length >= creditsRemaining) {
      setFormError(t("register.prepaidMode.limitReached"));
      return;
    }

    if (!selectedAthlete) {
      setFormError(t("register.error.noAthlete"));
      return;
    }
    if (!selectedClub) {
      setFormError(t("register.error.noClub"));
      return;
    }
    if (authorizedClubNames.length > 0 && !authorizedClubNames.includes(selectedClub)) {
      setFormError(t("register.error.unauthorizedClub"));
      return;
    }
    if (!selectedCategory) {
      setFormError(t("register.error.noCategory"));
      return;
    }
    if (!selectedWeight) {
      setFormError(t("register.error.noWeight"));
      return;
    }

    const isDuplicateInBasket = registrationList.some(
      (r) => r.athleteId === selectedAthlete.athleteId
    );
    if (isDuplicateInBasket) {
      setFormError(
        `${selectedAthlete.firstName} ${selectedAthlete.lastName} ${t("register.error.duplicate")}`
      );
      return;
    }

    if (currentUser && competitionId) {
      setCheckingDuplicate(true);
      try {
        const alreadyRegistered = await isAthleteRegisteredByCoach(
          selectedAthlete.athleteId,
          competitionId,
          currentUser.uid
        );
        if (alreadyRegistered) {
          setFormError(
            `${selectedAthlete.firstName} ${selectedAthlete.lastName} ${t("register.error.alreadyRegistered")}`
          );
          return;
        }
      } catch (err) {
        console.error("Duplicate check failed:", err);
      } finally {
        setCheckingDuplicate(false);
      }
    }

   

    const newEntry: RegistrationItem = {
      athleteId: selectedAthlete.athleteId,
      firstName: selectedAthlete.firstName,
      lastName: selectedAthlete.lastName,
      dateOfBirth: selectedAthlete.dateOfBirth,
      club: selectedClub,
      division: selectedDivision,
      weightCategory: selectedWeight,
      category: selectedCategory,
      price: currentPrice,
    };

    setRegistrationList((prev) => [...prev, newEntry]);
    setSelectedAthlete(null);
    setSelectedClub("");
    setSelectedWeight("");
    setDuplicateWarning("");
    setFormError("");
  };

  const handleRemove = (athleteId: string) => {
    setRegistrationList((prev) => prev.filter((r) => r.athleteId !== athleteId));
  };

  const handleProceedToPayment = () => {
    setSaveError("");

    // ── Refresh price tier ────────────────────────────────────────────────
    // The unit price is computed once on mount. If the coach left the page
    // open across the early/mid deadline boundary, the basket prices are
    // stale. Recompute against the current server-clock-aligned `today` and
    // align every basket item to that price. Show a banner inside the
    // modal so the coach notices the change.
    if (competition) {
      const livePrice = calculatePrice(competition as any);
      if (livePrice !== currentPrice) {
        setCurrentPrice(livePrice);
        setRegistrationList((prev) => prev.map((r) => ({ ...r, price: livePrice })));
        setPriceTierChanged(true);
      } else {
        setPriceTierChanged(false);
      }
    } else {
      setPriceTierChanged(false);
    }

    // Auto-set payment method based on mode
    if (prepaidMode && creditPackage) {
      setPaymentMethod("prepaid");
    } else if (paymentMethod === "prepaid" && (!creditPackage || creditsRemaining < registrationList.length)) {
      setPaymentMethod("online");
    }
    setShowConfirm(true);
  };

  const handleConfirmRegistration = async () => {
    if (!currentUser || !competition) return;
    setSaving(true);
    setSaveError("");

    const effectivePrepaid = prepaidMode || paymentMethod === "prepaid";

    try {
      if (effectivePrepaid && creditPackage) {
        // ── Prepaid flow ────────────────────────────────────────────────────
        const referenceCode = await createPrepaidRegistrationBatch(
          currentUser.uid,
          currentUser.email ?? "",
          competition.id,
          competition.name,
          registrationList.map((r) => ({
            athleteId: r.athleteId,
            athleteSnapshot: {
              firstName: r.firstName,
              lastName: r.lastName,
              dateOfBirth: r.dateOfBirth,
            },
            club: r.club,
            weightCategory: r.weightCategory,
            division: r.division,
            category: r.category,
            price: r.price,
          })),
          creditPackage.id!,
          currentUser.displayName ?? ""
        );

        // Send prepaid confirmation email — non-blocking
        if (currentUser.email) {
          try {
            const functions = getFunctions(undefined, "europe-central2");
            const sendEmail = httpsCallable(functions, "sendEmail");
            await sendEmail({
              to: currentUser.email,
              subject: `Registration Confirmed / რეგისტრაცია დადასტურდა — ${competition.name} [${referenceCode}]`,
              html: buildPrepaidConfirmationEmailHtml({
                coachEmail: currentUser.email,
                competitionName: competition.name,
                referenceCode,
                athletes: registrationList,
              }),
            });
          } catch (emailErr) {
            console.error("Prepaid email failed (non-blocking):", emailErr);
          }
        }

        navigate("/success", {
          state: {
            referenceCode,
            competitionName: competition.name,
            athletes: registrationList.map((r) => ({
              athleteId: r.athleteId,
              slug: "",
              firstName: r.firstName,
              lastName: r.lastName,
              dateOfBirth: r.dateOfBirth,
              club: r.club,
              division: r.division,
              weightCategory: r.weightCategory,
              category: r.category,
              price: r.price,
            })),
            totalAmount,
          },
        });
      } else {
        // ── Real BOG payment flow ────────────────────────────────────────────
        // 1. Call the Cloud Function to create a BOG order
        // 2. Receive the redirect URL from BOG
        // 3. Hard-redirect the browser to BOG's payment page
        // 4. BOG handles the payment and calls our webhook (bogWebhook)
        // 5. bogWebhook writes registrations and sends the confirmation email
        // 6. BOG redirects the coach to /success or /payment-failed

        const functions = getFunctions(undefined, "europe-central2");
        const createBogOrderFn = httpsCallable(functions, "createBogOrder");

        const result = await createBogOrderFn({
          competitionId: competition.id,
          competitionName: competition.name,
          athletes: registrationList.map((r) => ({
            athleteId:      r.athleteId,
            firstName:      r.firstName,
            lastName:       r.lastName,
            dateOfBirth:    r.dateOfBirth,
            club:           r.club,
            division:       r.division,
            weightCategory: r.weightCategory,
            category:       r.category,
            price:          r.price,
          })),
          totalAmount,
          coachId:    currentUser.uid,
          coachEmail: currentUser.email ?? "",
        });

        const { redirectUrl, orderId } = result.data as {
          redirectUrl: string;
          orderId: string;
          bogOrderId: string;
        };

        // Persist context for the post-redirect pages. BOG sends the coach
        // back to /success or /payment-failed with no React state — these
        // keys let PaymentSuccess show the reference and PaymentFailed
        // offer a retry link straight to the same competition.
        try {
          sessionStorage.setItem("pendingOrderId", orderId);
          sessionStorage.setItem("pendingCompetitionId", competition.id);
          sessionStorage.setItem("pendingCompetitionName", competition.name);
        } catch {
          // sessionStorage may be unavailable (private mode, quota) — non-fatal.
        }

        // Hard redirect — this leaves the React app and goes to BOG's payment page.
        // Do NOT use navigate() here. After payment, BOG sends the coach back to
        // /success or /payment-failed and calls our bogWebhook server-side.
        window.location.href = redirectUrl;

        // Do not set saving(false) here — the page is navigating away.
        return;
      }
    } catch (err) {
      console.error("Registration save failed:", err);
      setSaveError(
        err instanceof Error ? err.message : t("register.save.error")
      );
      setSaving(false);
    }
  };

  const totalAmount = registrationList.reduce((sum, r) => sum + r.price, 0);

  // ---------------------------------------------------------------------------
  // Loading / not found
  // ---------------------------------------------------------------------------

  if (pageLoading) {
    return (
      <div style={s.page}>
        <Navbar />
        <div style={s.centeredMessage}>{t("common.loading")}</div>
      </div>
    );
  }

  if (!competition) {
    return (
      <div style={s.page}>
        <Navbar />
        <div style={s.centeredMessage}>Competition not found.</div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div style={s.page} className="page-content">
      <Navbar />

      {/* Confirmation Modal */}
      {showConfirm && (
        <ConfirmModal
          competition={competition}
          registrationList={registrationList}
          totalAmount={totalAmount}
          saving={saving}
          onConfirm={handleConfirmRegistration}
          onCancel={() => {
            setShowConfirm(false);
            setPriceTierChanged(false);
          }}
          t={t}
          gel={gel}
          creditPackage={creditPackage}
          prepaidMode={prepaidMode}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={setPaymentMethod}
          priceTierChanged={priceTierChanged}
        />
      )}

      {showCreateModal && (
        <div style={ms.overlay}>
          <div style={{ ...ms.modal, maxWidth: "420px" }}>
            <div style={ms.accentBar} />
            <div style={ms.modalBody}>
              <div style={ms.modalTitle}>{t("register.createAthleteModal.title")}</div>
              <div style={{ fontSize: "13px", color: C.text2, marginBottom: "12px" }}>
                {t("register.createAthleteModal.subtitle")}
              </div>
              <div style={{
                fontSize: "12px",
                color: C.warning,
                marginBottom: "16px",
                padding: "8px 12px",
                background: C.warningDim,
                borderRadius: "6px",
              }}>
                ⚠️ {t("register.createAthleteModal.warning")}
              </div>

              {/* First name */}
              <div style={{ marginBottom: "12px" }}>
                <div style={s.fieldLabel}>{t("register.createAthleteModal.firstName")} *</div>
                <input
                  style={{ ...s.searchInput, marginTop: "6px" }}
                  value={createForm.firstName}
                  onChange={e => { setCreateForm(p => ({ ...p, firstName: e.target.value })); setDuplicateOverrideConfirmed(false); setDuplicateOverrideReason(""); }}
                  placeholder="e.g. გიორგი"
                  autoFocus
                />
              </div>

              {/* Last name */}
              <div style={{ marginBottom: "12px" }}>
                <div style={s.fieldLabel}>{t("register.createAthleteModal.lastName")} *</div>
                <input
                  style={{ ...s.searchInput, marginTop: "6px" }}
                  value={createForm.lastName}
                  onChange={e => { setCreateForm(p => ({ ...p, lastName: e.target.value })); setDuplicateOverrideConfirmed(false); setDuplicateOverrideReason(""); }}
                  placeholder="e.g. ბერიძე"
                />
              </div>

              {/* Date of birth */}
              <div style={{ marginBottom: "16px" }}>
                <div style={s.fieldLabel}>
                  {t("register.createAthleteModal.dob")} ({t("register.createAthleteModal.optional")})
                </div>
                <input
                  style={{ ...s.searchInput, marginTop: "6px" }}
                  type="date"
                  value={createForm.dateOfBirth}
                  onChange={e => { setCreateForm(p => ({ ...p, dateOfBirth: e.target.value })); setDuplicateOverrideConfirmed(false); setDuplicateOverrideReason(""); }}
                />
              </div>

              {/* Auto-generate note */}
              <div style={{
                fontSize: "12px",
                color: C.text2,
                marginBottom: "16px",
                padding: "8px 12px",
                background: C.surface2,
                borderRadius: "6px",
              }}>
                {t("register.createAthleteModal.autoNote")}
              </div>

              {possibleCreateMatches.length > 0 && (
                <div style={s.possibleMatches}>
                  <strong>{t("register.createAthleteModal.possibleMatches")}</strong>
                  <span>{t("register.createAthleteModal.possibleMatchesHelp")}</span>
                  {possibleCreateMatches.slice(0, 5).map(({ athlete, strength }) => (
                    <div key={athlete.id || athlete.athleteId} style={s.possibleMatchRow}>
                      <div>
                        <b>{athlete.firstName} {athlete.lastName}</b>
                        <small>{athlete.athleteId || "—"} · {athlete.dateOfBirth || t("register.createAthleteModal.dobUnknown")} · {athlete.club || t("register.createAthleteModal.clubUnknown")}</small>
                      </div>
                      <button type="button" style={s.chooseExistingButton} onClick={() => {
                        setShowCreateModal(false);
                        setCreateForm({ firstName: "", lastName: "", dateOfBirth: "" });
                        setDuplicateOverrideConfirmed(false);
                        setDuplicateOverrideReason("");
                        handleSelectAthlete(athlete as Athlete);
                      }}>{t("register.createAthleteModal.chooseExisting")}</button>
                      {strength === "strong" && <em>{t("register.createAthleteModal.strongMatch")}</em>}
                    </div>
                  ))}
                  {duplicateOverrideConfirmed && hasStrongCreateMatch && (
                    <label style={{ display: "block", marginTop: 10 }}>
                      <span style={s.fieldLabel}>{t("register.createAthleteModal.overrideReason")}</span>
                      <input style={{ ...s.searchInput, marginTop: 6 }} value={duplicateOverrideReason}
                        onChange={(event) => setDuplicateOverrideReason(event.target.value)}
                        placeholder={t("register.createAthleteModal.overrideReasonHint")} />
                    </label>
                  )}
                </div>
              )}

              {createError && <div style={s.errorBox}>{createError}</div>}

              <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
                <button
                  style={{ ...ms.cancelBtn, flex: 1 }}
                  onClick={() => { setShowCreateModal(false); setCreateError(""); setDuplicateOverrideConfirmed(false); setDuplicateOverrideReason(""); }}
                  disabled={createLoading}
                >
                  {t("register.confirm.cancel")}
                </button>
                <button
                  style={{ ...(createLoading ? ms.confirmBtnDisabled : ms.confirmBtn), flex: 1 }}
                  onClick={handleCreateAthlete}
                  disabled={createLoading}
                >
                  {createLoading
                    ? t("register.saving")
                    : possibleCreateMatches.length > 0
                    ? t("register.createAthleteModal.createAnyway")
                    : t("register.createAthleteModal.submit")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={s.container}>
        {/* Back link */}
        <span style={s.backLink} onClick={() => navigate("/dashboard")}>
          ← {t("register.back")}
        </span>

        {/* ── Prepaid mode toggle ──────────────────────────────────────────── */}
        {creditPackage && creditsRemaining > 0 && (
          <div style={prepaidMode ? s.prepaidBannerActive : s.prepaidBannerIdle}>
            {!prepaidMode ? (
              <button style={s.activatePrepaidBtn} onClick={handleActivatePrepaidMode}>
                🎟️{" "}
                {t("register.prepaidMode.activate").replace(
                  "{remaining}",
                  String(creditsRemaining)
                )}
              </button>
            ) : (
              <div style={s.prepaidActiveRow}>
                <div style={s.prepaidActiveLabel}>
                  <span style={s.prepaidActiveIcon}>🎟️</span>
                  <div>
                    <div style={s.prepaidActiveTitle}>
                      {t("register.prepaidMode.activeTitle")}
                    </div>
                    <div style={s.prepaidActiveSub}>
                      {t("register.prepaidMode.counter")
                        .replace("{used}", String(registrationList.length))
                        .replace("{total}", String(creditsRemaining))}
                    </div>
                  </div>
                </div>
                <button style={s.exitPrepaidBtn} onClick={handleExitPrepaidMode}>
                  {t("register.prepaidMode.exit")}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Competition banner */}
        <div style={s.competitionBanner}>
          <div style={s.bannerLeft}>
            <div style={s.bannerName}>{competition.name}</div>
            <div style={s.bannerSub}>
              {competition.startDate && formatDate(competition.startDate, locale)}
              {competition.endDate && ` – ${formatDate(competition.endDate, locale)}`}
            </div>
          </div>
          <div style={s.bannerRight}>
            <div style={s.priceBadge}>
              {prepaidMode ? (
                <>
                  <span style={{ textDecoration: "line-through", opacity: 0.5, fontSize: "16px" }}>
                    {currentPrice}
                  </span>{" "}
                  <span style={{ color: "var(--color-success)" }}>0</span>
                </>
              ) : (
                currentPrice
              )}{" "}
              {gel}
            </div>
            <div style={s.priceLabel}>{t("register.pricePerAthlete")}</div>
          </div>
        </div>

        {/* Athlete search card */}
        <div style={s.card}>
          <div style={s.cardTitle}>{t("register.addAthlete")}</div>

                    {/* Search input */}
          <div style={s.searchWarningBox}>
            ⚠️ {t("register.createAthleteModal.warning")}
          </div>
          <div style={s.searchWrapper}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                style={{ ...s.searchInput, flex: 1 }}
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (selectedAthlete) setSelectedAthlete(null);
                }}
                placeholder={
                  athletesLoading
                    ? t("register.searchLoading")
                    : t("register.searchPlaceholder")
                }
                disabled={athletesLoading}
                autoComplete="off"
              />
              <button
                type="button"
                style={{
                  padding: "10px 16px",
                  backgroundColor: C.surface2,
                  border: `1px solid ${C.border2}`,
                  borderRadius: "8px",
                  color: C.red,
                  fontWeight: 700,
                  fontSize: "13px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
                onClick={() => {
                  const parts = search.trim().split(/\s+/);
                  setCreateForm({
                    firstName: parts[0] ?? "",
                    lastName: parts.slice(1).join(" "),
                    dateOfBirth: "",
                  });
                  setCreateError("");
                  setShowCreateModal(true);
                }}
              >
                + {t("register.createAthlete")}
              </button>
            </div>
            {search.length >= 2 && !selectedAthlete && (filtered.length > 0 || !athletesLoading) && (
              <div style={s.resultsList}>
                {filtered.slice(0, 20).map((athlete) => (
                  <div
                    key={athlete.id}
                    style={
                      hoveredResult === athlete.id
                        ? s.resultItemHighlight
                        : s.resultItem
                    }
                    onMouseEnter={() => setHoveredResult(athlete.id)}
                    onMouseLeave={() => setHoveredResult("")}
                    onClick={() => handleSelectAthlete(athlete)}
                  >
                    <div>
                      {athlete.firstName} {athlete.lastName}
                    </div>
                    <div style={s.resultMeta}>
                      {athlete.dateOfBirth}
                    </div>
                  </div>
                ))}

                {/* "Create new athlete" item — always shown when search has 2+ chars */}
                {search.trim().length >= 2 && !athletesLoading && (
                  <div
                    style={hoveredResult === "__create__" ? s.resultItemHighlight : s.createItem}
                    onMouseEnter={() => setHoveredResult("__create__")}
                    onMouseLeave={() => setHoveredResult("")}
                    onClick={() => {
                      const parts = search.trim().split(/\s+/);
                      setCreateForm({
                        firstName: parts[0] ?? "",
                        lastName: parts.slice(1).join(" "),
                        dateOfBirth: "",
                      });
                      setCreateError("");
                      setShowCreateModal(true);
                    }}
                  >
                    ＋ {t("register.createAthlete")} "{search.trim()}"
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Selected athlete display */}
          {selectedAthlete && (
            <div style={s.selectedAthlete}>
              <div>
                <div style={s.selectedName}>
                  {selectedAthlete.firstName} {selectedAthlete.lastName}
                </div>
                <div style={s.selectedMeta}>{selectedAthlete.dateOfBirth}</div>
              </div>
              <button
                style={s.clearBtn}
                onClick={() => {
                  setSelectedAthlete(null);
                  setDuplicateWarning("");
                }}
              >
                ×
              </button>
            </div>
          )}

          {duplicateWarning && (
            <div style={s.warningBox}>{duplicateWarning}</div>
          )}

          {/* Club selector */}
          <div style={{ ...s.formGrid, marginTop: "16px" }}>
            <div style={s.fieldGroup}>
              <div style={s.fieldLabel}>{t("register.club.placeholder")}</div>
              <div style={s.searchWrapper}>
                <input
                  style={s.searchInput}
                  type="text"
                  value={selectedClub}
                  onChange={(e) => setSelectedClub(e.target.value)}
                  onFocus={() => { if (authorizedClubNames.length !== 1) setClubFocused(true); }}
                  onBlur={() => setTimeout(() => setClubFocused(false), 150)}
                  placeholder={t("register.selectClub")}
                  readOnly={authorizedClubNames.length === 1}
                />
                {clubFocused && filteredClubs.length > 0 && (
                  <div style={s.resultsList}>
                    {filteredClubs.slice(0, 15).map((c: any) => (
                      <div
                        key={c.id}
                        style={
                          hoveredResult === c.id
                            ? s.resultItemHighlight
                            : s.resultItem
                        }
                        onMouseEnter={() => setHoveredResult(c.id)}
                        onMouseLeave={() => setHoveredResult("")}
                        onClick={() => setSelectedClub(c.name)}
                      >
                        {c.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {authorizedClubNames.length === 1 && <span style={s.fieldHelp}>{t("register.club.assignedAutomatically")}</span>}
              {authorizedClubNames.length > 1 && <span style={s.fieldHelp}>{t("register.club.chooseAuthorized")}</span>}
              {authorizedClubNames.length === 0 && <span style={s.fieldHelp}>{t("register.club.legacySelection")}</span>}
            </div>

            {/* Category */}
            <div style={s.fieldGroup}>
              <div style={s.fieldLabel}>{t("register.category")}</div>
              <select
                style={s.select}
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                disabled={typeOptions.length === 1}
              >
                <option value="">{t("register.selectCategory")}</option>
                {typeOptions.map((type) => (
                  <option key={type} value={type}>
                    {categoryDisplayLabel(type)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Division + Weight */}
          <div style={s.formGrid}>
            <div style={s.fieldGroup}>
              <div style={s.fieldLabel}>{t("register.division.male")} / {t("register.division.female")}</div>
              <div style={s.divisionToggle}>
                {(["Male", "Female"] as const).map((d) => (
                  <button
                    key={d}
                    style={{
                      ...s.divisionBtnBase,
                      ...(selectedDivision === d
                        ? s.divisionBtnActive
                        : s.divisionBtnInactive),
                    }}
                    onClick={() => setSelectedDivision(d)}
                  >
                    {d === "Male"
                      ? t("register.division.male")
                      : t("register.division.female")}
                  </button>
                ))}
              </div>
            </div>

            <div style={s.fieldGroup}>
              <div style={s.fieldLabel}>{t("register.weight.placeholder")}</div>
              <WeightSelect
                value={selectedWeight}
                onChange={setSelectedWeight}
                weights={weightOptions}
                recommended={getRecommendedWeights(selectedCategory, selectedDivision)}
                category={selectedCategory}
                division={selectedDivision}
                placeholder={t("register.selectWeight")}
                selectStyle={s.select}
                t={t}
              />
            </div>
          </div>

          {formError && <div style={s.errorBox}>{formError}</div>}

          <button
            style={{
              ...s.addBtn,
              ...(prepaidLimitReached || checkingDuplicate ? s.addBtnDisabled : {}),
            }}
            onClick={handleAddAthlete}
            disabled={checkingDuplicate || prepaidLimitReached}
          >
            {checkingDuplicate
              ? t("register.checking")
              : prepaidLimitReached
              ? t("register.prepaidMode.limitReachedBtn")
              : t("register.addToBasket")}
          </button>
        </div>

        {/* Registration basket */}
        <div style={s.card}>
          {/* Basket header with optional credit counter */}
          <div style={s.basketHeader}>
            <div style={s.cardTitle}>
              {t("register.basket")} ({registrationList.length})
            </div>
            {prepaidMode && (
              <div style={s.creditCounterBadge}>
                {t("register.prepaidMode.counter")
                  .replace("{used}", String(registrationList.length))
                  .replace("{total}", String(creditsRemaining))}
              </div>
            )}
          </div>

          {registrationList.length === 0 ? (
            <div style={s.emptyList}>{t("register.basketEmpty")}</div>
          ) : (
            <>
            <div className="registration-basket-desktop">
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>{t("register.table.num")}</th>
                  <th style={s.th}>{t("register.table.athlete")}</th>
                  <th style={s.th}>{t("register.table.club")}</th>
                  <th style={s.th}>{t("register.table.ageGroup")}</th>
                  <th style={s.th}>{t("register.table.division")}</th>
                  <th style={s.th}>{t("register.table.weight")}</th>
                  <th style={s.th}>{t("register.table.price")}</th>
                  <th style={s.th}></th>
                </tr>
              </thead>
              <tbody>
                {registrationList.map((item, idx) => (
                  <tr key={item.athleteId}>
                    <td style={s.td}>{idx + 1}</td>
                    <td style={{ ...s.td, fontWeight: 600, color: C.text }}>
                      {item.firstName} {item.lastName}
                    </td>
                    <td style={s.td}>{item.club}</td>
                    <td style={s.td}>{categoryDisplayLabel(item.category)}</td>
                    <td style={s.td}>{item.division}</td>
                    <td style={s.td}>{item.weightCategory}</td>
                    <td style={{ ...s.td, fontWeight: 700, color: C.text }}>
                      {prepaidMode ? (
                        <span style={{ textDecoration: "line-through", color: "var(--color-text-subtle)" }}>
                          {item.price} {gel}
                        </span>
                      ) : (
                        `${item.price} ${gel}`
                      )}
                    </td>
                    <td style={s.td}>
                      <button
                        style={s.removeBtn}
                        onClick={() => handleRemove(item.athleteId)}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="registration-basket-mobile">
              {registrationList.map((item, idx) => (
                <article className="registration-basket-card" key={item.athleteId}>
                  <div className="registration-basket-card-index">{idx + 1}</div>
                  <div className="registration-basket-card-content">
                    <strong>{item.firstName} {item.lastName}</strong>
                    <span className="registration-basket-card-club">{item.club}</span>
                    <span className="registration-basket-card-category">
                      {categoryDisplayLabel(item.category)} · {item.division} · {item.weightCategory}
                    </span>
                  </div>
                  <div className="registration-basket-card-actions">
                    <strong className={prepaidMode ? "is-prepaid" : ""}>{item.price} {gel}</strong>
                    <button
                      type="button"
                      aria-label={`${t("register.removeAthlete")} ${item.firstName} ${item.lastName}`}
                      onClick={() => handleRemove(item.athleteId)}
                    >
                      ×
                    </button>
                  </div>
                </article>
              ))}
            </div>
            </>
          )}

          {/* Prepaid basket summary */}
          {prepaidMode && registrationList.length > 0 && (
            <div style={s.prepaidBasketSummary}>
              🎟️ {t("register.prepaidMode.basketSummary")}
            </div>
          )}
        </div>

        {/* Total + proceed bar */}
        {registrationList.length > 0 && (
          <>
            {/* Insufficient credits warning (non-prepaid mode) */}
            {!prepaidMode &&
              creditPackage &&
              creditsRemaining < registrationList.length &&
              creditsRemaining > 0 && (
                <div style={s.creditWarningBar}>
                  ⚠️{" "}
                  {t("register.payment.insufficientCredits")
                    .replace("{remaining}", String(creditsRemaining))
                    .replace(
                      "{needed}",
                      String(registrationList.length - creditsRemaining)
                    )}
                </div>
              )}

            <div style={s.totalBar}>
              <div>
                <div style={s.totalLabel}>{t("register.total")}</div>
                <div style={s.totalAmount}>
                  {prepaidMode ? (
                    <>
                      <span style={{ textDecoration: "line-through", color: "var(--color-text-subtle)", fontSize: "18px" }}>
                        {totalAmount} {gel}
                      </span>
                      {"  "}
                      <span style={{ color: "var(--color-success)", fontWeight: 800 }}>0 {gel}</span>
                    </>
                  ) : (
                    `${totalAmount} ${gel}`
                  )}
                </div>
                <div style={s.totalCount}>
                  {registrationList.length} {t("register.athletes")}
                </div>
              </div>
              <button
                style={s.proceedBtn}
                onClick={handleProceedToPayment}
              >
                {t("register.proceedBtn")}
              </button>
            </div>
          </>
        )}

        {saveError && <div style={{ ...s.errorBox, marginTop: "12px" }}>{saveError}</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal styles
// ---------------------------------------------------------------------------

const ms: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: "16px",
  },
  modal: {
    backgroundColor: C.surface,
    borderRadius: "14px",
    overflow: "hidden",
    width: "100%",
    maxWidth: "560px",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  },
  accentBar: { height: "4px", backgroundColor: C.red },
  modalBody: {
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  modalHeader: { display: "flex", flexDirection: "column", gap: "4px" },
  modalTitle: {
    fontSize: "18px",
    fontWeight: 700,
    color: C.text,
    fontFamily: FONT.display,
    letterSpacing: "0.5px",
  },
  modalSubtitle: { fontSize: "13px", color: C.text2 },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px 16px",
    backgroundColor: C.surface2,
    borderRadius: "8px",
    fontSize: "13px",
    border: `1px solid ${C.border}`,
  },
  infoLabel: { color: C.text2, fontWeight: 600 },
  infoValue: { color: C.text, fontWeight: 700 },
  athletesSection: {
    border: `1px solid ${C.border}`,
    borderRadius: "10px",
    overflow: "hidden",
  },
  athletesSectionLabel: {
    padding: "10px 16px",
    backgroundColor: C.surface2,
    fontSize: "11px",
    fontWeight: 700,
    color: C.text2,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: `1px solid ${C.border}`,
  },
  athletesList: { maxHeight: "200px", overflowY: "auto" },
  athleteRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 16px",
    borderBottom: `1px solid ${C.border}`,
    fontSize: "13px",
  },
  athleteRowLeft: { display: "flex", alignItems: "center", gap: "10px" },
  athleteIndex: {
    width: "22px",
    height: "22px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: C.surface2,
    borderRadius: "50%",
    fontSize: "11px",
    fontWeight: 700,
    color: C.text2,
    flexShrink: 0,
  },
  athleteName: { fontWeight: 600, color: C.text },
  athleteMeta: { fontSize: "12px", color: C.text2, marginTop: "2px" },
  athletePrice: { fontWeight: 700, color: C.text, fontSize: "13px" },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 16px",
    backgroundColor: C.surface2,
    borderRadius: "10px",
    border: `1px solid ${C.border}`,
  },
  totalLabel: { fontSize: "13px", color: C.text2, fontWeight: 600 },
  totalAmount: { fontSize: "20px", fontWeight: 800, color: C.text },

  // Prepaid mode info box in modal
  prepaidInfoBox: {
    backgroundColor: C.successDim,
    border: "1px solid #bbf7d0",
    borderRadius: "10px",
    padding: "14px 16px",
  },
  prepaidInfoTitle: {
    fontSize: "13px",
    fontWeight: 700,
    color: C.success,
    marginBottom: "4px",
  },
  prepaidInfoSub: {
    fontSize: "13px",
    color: C.success,
    lineHeight: 1.6,
  },

  // Payment method section (shown only when NOT in prepaidMode but credits cover basket)
  paymentSection: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  paymentSectionLabel: {
    fontSize: "11px",
    fontWeight: 700,
    color: C.text2,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  paymentOptions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
  },
  paymentOptionBtn: {
    padding: "12px 14px",
    borderRadius: "10px",
    cursor: "pointer",
    textAlign: "left" as const,
    transition: "all 0.15s",
    border: `2px solid ${C.border}`,
  },
  paymentOptionActive: {
    backgroundColor: C.surface2,
    border: `2px solid ${C.red}`,
  },
  paymentOptionPrepaidActive: {
    backgroundColor: C.successDim,
    border: `2px solid ${C.successBorder}`,
  },
  paymentOptionInactive: {
    backgroundColor: C.surface,
    border: `2px solid ${C.border}`,
  },
  paymentOptionTitle: {
    fontSize: "13px",
    fontWeight: 700,
    color: C.text,
    marginBottom: "3px",
  },
  paymentOptionSub: {
    fontSize: "11px",
    color: C.text2,
    lineHeight: 1.4,
  },
  creditWarning: {
    backgroundColor: C.warningDim,
    border: `1px solid ${C.warningBorder}`,
    borderRadius: "8px",
    padding: "10px 14px",
    fontSize: "13px",
    color: "var(--color-warning)",
  },
  actions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    paddingTop: "4px",
  },
  cancelBtn: {
    padding: "11px 20px",
    borderRadius: "8px",
    border: `1px solid ${C.border2}`,
    backgroundColor: C.surface,
    color: C.text2,
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: 600,
  },
  confirmBtn: {
    padding: "11px 24px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: C.red,
    color: "#fff",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: 700,
    letterSpacing: "0.3px",
  },
  confirmBtnDisabled: {
    padding: "11px 24px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: C.border2,
    color: C.text3,
    fontSize: "14px",
    cursor: "not-allowed",
    fontWeight: 700,
    letterSpacing: "0.3px",
  },

  // ←←← ADD THIS BLOCK HERE
  prepaidOverLimitWarning: {
    backgroundColor: "var(--color-danger-soft)",
    border: `1px solid ${C.dangerBorder}`,
    borderRadius: "8px",
    padding: "10px 14px",
    fontSize: "13px",
    color: "#991b1b",
    fontWeight: 600,
  },
};

// ---------------------------------------------------------------------------
// Page styles
// ---------------------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    backgroundColor: C.bg,
    fontFamily: FONT.body,
  },
  container: {
    maxWidth: "720px",
    margin: "0 auto",
    padding: "24px 20px 60px",
  },
  backLink: {
    fontSize: "13px",
    color: C.text2,
    cursor: "pointer",
    display: "inline-block",
    marginBottom: "20px",
  },

  // ── Prepaid mode toggle elements ──────────────────────────────────────────
  prepaidBannerIdle: {
    marginBottom: "16px",
  },
  prepaidBannerActive: {
    backgroundColor: C.successDim,
    border: `1.5px solid ${C.successBorder}`,
    borderRadius: "12px",
    padding: "14px 18px",
    marginBottom: "16px",
  },
  activatePrepaidBtn: {
    width: "100%",
    padding: "13px 18px",
    backgroundColor: C.successDim,
    border: `1.5px dashed ${C.successBorder}`,
    borderRadius: "10px",
    color: C.success,
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "left" as const,
    letterSpacing: "0.2px",
    transition: "background 0.15s",
  },
  prepaidActiveRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap" as const,
    gap: "10px",
  },
  prepaidActiveLabel: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  prepaidActiveIcon: {
    fontSize: "22px",
  },
  prepaidActiveTitle: {
    fontSize: "14px",
    fontWeight: 700,
    color: C.success,
  },
  prepaidActiveSub: {
    fontSize: "12px",
    color: C.success,
    opacity: 0.85,
    marginTop: "2px",
  },
  exitPrepaidBtn: {
    padding: "7px 14px",
    backgroundColor: "transparent",
    border: `1px solid ${C.successBorder}`,
    borderRadius: "7px",
    color: C.success,
    fontSize: "12px",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  },

  // ── Competition banner ────────────────────────────────────────────────────
  competitionBanner: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "12px",
    padding: "20px 24px",
    marginBottom: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap" as const,
    gap: "12px",
  },
  bannerLeft: { flex: 1 },
  bannerName: {
    fontSize: "18px",
    fontWeight: 700,
    color: C.text,
    fontFamily: FONT.display,
    marginBottom: "4px",
  },
  bannerSub: { fontSize: "13px", color: C.text2 },
  bannerRight: { textAlign: "right" as const },
  priceBadge: {
    fontSize: "22px",
    fontWeight: 800,
    color: C.text,
    lineHeight: 1,
  },
  priceLabel: { fontSize: "11px", color: C.text2, marginTop: "4px" },

  // ── Cards ─────────────────────────────────────────────────────────────────
  card: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "16px",
  },
  cardTitle: {
    fontSize: "15px",
    fontWeight: 700,
    color: C.text,
    marginBottom: "16px",
    fontFamily: FONT.display,
  },

  // ── Basket header ──────────────────────────────────────────────────────────
  basketHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    flexWrap: "wrap" as const,
    gap: "8px",
  },
  creditCounterBadge: {
    backgroundColor: "var(--color-success-soft)",
    border: "1px solid #6ee7b7",
    borderRadius: "20px",
    padding: "4px 12px",
    fontSize: "12px",
    fontWeight: 700,
    color: "var(--color-success)",
  },

  // ── Prepaid basket summary ────────────────────────────────────────────────
  prepaidBasketSummary: {
    backgroundColor: C.successDim,
    border: "1px solid #bbf7d0",
    borderRadius: "8px",
    padding: "10px 14px",
    fontSize: "13px",
    fontWeight: 600,
    color: C.success,
    marginTop: "12px",
  },

  // ── Form elements ─────────────────────────────────────────────────────────
   searchWarningBox: { backgroundColor: C.warningDim, border: `1px solid ${C.warningBorder}`, borderRadius: "6px", padding: "8px 12px", fontSize: "12px", color: C.warning, marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px" },
  searchWrapper: { position: "relative" as const },
  searchInput: {
    width: "100%",
    padding: "12px 16px",
    border: `1px solid ${C.border2}`,
    borderRadius: "8px",
    // 16px keeps iOS/Android from zooming the page when the field is focused,
    // which is what made the dropdown "jump" on smaller phones.
    fontSize: "16px",
    outline: "none",
    boxSizing: "border-box" as const,
    color: C.text,
    backgroundColor: C.surface2,
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  resultsList: {
    position: "absolute" as const,
    top: "calc(100% + 2px)",
    left: 0,
    right: 0,
    backgroundColor: C.surface,
    border: `1px solid ${C.border2}`,
    borderRadius: "8px",
    maxHeight: "min(300px, 50vh)",
    overflowY: "auto" as const,
    WebkitOverflowScrolling: "touch" as any,
    zIndex: 100,
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
  },
  resultItem: {
    padding: "12px 16px",
    minHeight: "44px",
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "14px",
    color: C.text,
    borderBottom: `1px solid ${C.border}`,
  },
  resultItemHighlight: {
    padding: "12px 16px",
    minHeight: "44px",
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "14px",
    color: C.text,
    borderBottom: `1px solid ${C.border}`,
    backgroundColor: C.surface2,
  },
  createItem: {
    padding: "10px 16px",
    cursor: "pointer",
    fontSize: "13px",
    color: C.red,
    fontWeight: 600,
    borderTop: `1px solid ${C.border}`,
  },
  resultMeta: { color: C.text2, fontSize: "12px" },
  selectedAthlete: {
    backgroundColor: C.surface2,
    border: `1px solid ${C.text}`,
    borderRadius: "8px",
    padding: "12px 16px",
    marginTop: "8px",
    marginBottom: "4px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  selectedName: { fontWeight: 700, color: C.text, fontSize: "14px" },
  selectedMeta: { fontSize: "12px", color: C.text2, marginTop: "2px" },
  clearBtn: {
    background: "none",
    border: "none",
    color: C.red,
    cursor: "pointer",
    fontSize: "20px",
    fontWeight: 700,
    lineHeight: 1,
    padding: "0 4px",
  },
  warningBox: {
    backgroundColor: C.warningDim,
    border: `1px solid ${C.warning}`,
    borderRadius: "8px",
    padding: "10px 14px",
    fontSize: "13px",
    color: "#856404",
    marginTop: "8px",
  },
  errorBox: {
    backgroundColor: C.redDim,
    border: `1px solid ${C.red}`,
    borderRadius: "8px",
    padding: "10px 14px",
    fontSize: "13px",
    color: C.red,
    marginTop: "12px",
  },
  possibleMatches: {
    padding: "12px",
    marginBottom: "14px",
    border: `1px solid ${C.warning}`,
    borderRadius: "8px",
    backgroundColor: C.warningDim,
    color: C.text,
    display: "flex",
    flexDirection: "column",
    gap: "7px",
    fontSize: "12px",
  },
  possibleMatchRow: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    padding: "9px",
    borderRadius: "7px",
    backgroundColor: C.surface,
  },
  chooseExistingButton: {
    flexShrink: 0,
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    padding: "7px 9px",
    backgroundColor: C.surface2,
    color: C.text,
    fontWeight: 700,
    cursor: "pointer",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    marginTop: "16px",
    marginBottom: "16px",
  },
  fieldGroup: { display: "flex", flexDirection: "column" as const, gap: "6px" },
  fieldHelp: { color: C.text3, fontSize: "11px", lineHeight: 1.4 },
  fieldLabel: {
    fontSize: "11px",
    fontWeight: 700,
    color: C.text2,
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  select: {
    padding: "12px 14px",
    border: `1px solid ${C.border2}`,
    borderRadius: "8px",
    fontSize: "16px",
    color: C.text,
    backgroundColor: C.surface2,
    outline: "none",
    cursor: "pointer",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  divisionToggle: { display: "flex", gap: "8px" },
  divisionBtnBase: {
    flex: 1,
    padding: "11px",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: 600,
    border: `1px solid ${C.border2}`,
    transition: "all 0.15s",
  },
  divisionBtnActive: {
    backgroundColor: C.red,
    color: "#fff",
    border: `1px solid ${C.red}`,
  },
  divisionBtnInactive: {
    backgroundColor: C.surface,
    color: C.text2,
    border: `1px solid ${C.border2}`,
  },
  addBtn: {
    width: "100%",
    padding: "13px",
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.3px",
    marginTop: "16px",
  },
  addBtnDisabled: {
    backgroundColor: C.border2,
    color: C.text3,
    cursor: "not-allowed",
  },
  table: { width: "100%", borderCollapse: "collapse" as const },
  th: {
    textAlign: "left" as const,
    padding: "10px 12px",
    backgroundColor: C.surface2,
    color: C.text2,
    fontWeight: 700,
    fontSize: "11px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  td: {
    padding: "12px",
    borderBottom: `1px solid ${C.border}`,
    color: C.text2,
    verticalAlign: "middle" as const,
    fontSize: "13px",
  },
  removeBtn: {
    background: "none",
    border: "none",
    color: C.red,
    cursor: "pointer",
    fontSize: "18px",
    fontWeight: 700,
    padding: "0 4px",
    lineHeight: 1,
  },
  emptyList: {
    textAlign: "center" as const,
    padding: "36px 16px",
    color: C.text2,
    fontSize: "14px",
  },
  creditWarningBar: {
    backgroundColor: C.warningDim,
    border: `1px solid ${C.warningBorder}`,
    borderRadius: "8px",
    padding: "10px 16px",
    fontSize: "13px",
    color: "var(--color-warning)",
    marginBottom: "12px",
  },
  totalBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 28px",
    backgroundColor: C.surface,
    borderRadius: "12px",
    flexWrap: "wrap" as const,
    gap: "16px",
    border: `1px solid ${C.border}`,
  },
  totalLabel: { color: C.text2, fontSize: "13px", marginBottom: "4px" },
  totalAmount: { color: C.text, fontSize: "26px", fontWeight: 700, lineHeight: 1 },
  totalCount: { color: C.text2, fontSize: "12px", marginTop: "4px" },
  proceedBtn: {
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "14px 32px",
    fontSize: "15px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.3px",
  },
  centeredMessage: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "60vh",
    color: C.text2,
    fontSize: "15px",
  },
};
